import {FC, ReactNode} from 'react'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useLocation} from 'react-router'
import {checkIsActive, KTIcon, WithChildren} from '../../../helpers'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  onClick?: () => void
}

// Thin-line (outline) sidebar icons, all drawn on a 24x24 grid so they render consistently.
const SIDEBAR_ICONS: Record<string, ReactNode> = {
  'element-11': (
    <>
      <rect x='3' y='3' width='7' height='9' rx='1' />
      <rect x='14' y='3' width='7' height='5' rx='1' />
      <rect x='14' y='12' width='7' height='9' rx='1' />
      <rect x='3' y='16' width='7' height='5' rx='1' />
    </>
  ),
  'manage-roles': (
    <>
      <path d='M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' />
      <path d='m9 12 2 2 4-4' />
    </>
  ),
  'manage-buyers': (
    <>
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </>
  ),
  user: (
    <>
      <path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' />
      <circle cx='12' cy='7' r='4' />
    </>
  ),
  'all-product-icon': (
    <>
      <path d='m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z' />
      <path d='m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65' />
      <path d='m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65' />
    </>
  ),
  'product-management-list': (
    <>
      <rect x='8' y='2' width='8' height='4' rx='1' />
      <path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' />
      <path d='M12 11h4' />
      <path d='M12 16h4' />
      <path d='M8 11h.01' />
      <path d='M8 16h.01' />
    </>
  ),
  'notification-on': (
    <>
      <path d='M10.268 21a2 2 0 0 0 3.464 0' />
      <path d='M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326' />
    </>
  ),
  shop: (
    <>
      <path d='M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9' />
      <path d='M3 4h18l1 5a3 3 0 0 1-5.5 1.5 3 3 0 0 1-5 0 3 3 0 0 1-5 0A3 3 0 0 1 2 9z' />
      <path d='M9 20v-5h6v5' />
    </>
  ),
  'profile-circle': (
    <>
      <circle cx='12' cy='12' r='10' />
      <circle cx='12' cy='10' r='3' />
      <path d='M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662' />
    </>
  ),
  'product-icon': (
    <>
      <path d='M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z' />
      <path d='M12 22V12' />
      <path d='M3.29 7 12 12l8.71-5' />
      <path d='m7.5 4.27 9 5.15' />
    </>
  ),
  'setting-2': (
    <>
      <path d='M10 5H3' />
      <path d='M12 19H3' />
      <path d='M14 3v4' />
      <path d='M16 17v4' />
      <path d='M21 12h-9' />
      <path d='M21 19h-5' />
      <path d='M21 5h-7' />
      <path d='M8 10v4' />
      <path d='M8 12H3' />
    </>
  ),
  'price-mapping-settings': (
    <>
      <path d='M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z' />
      <circle cx='7.5' cy='7.5' r='.5' fill='currentColor' />
    </>
  ),
  logs: (
    <>
      <path d='M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z' />
      <path d='M14 2v5a1 1 0 0 0 1 1h5' />
      <path d='M10 9H8' />
      <path d='M16 13H8' />
      <path d='M16 17H8' />
    </>
  ),
}

const AsideMenuItem: FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet = false,
  onClick,
}) => {
  const {pathname} = useLocation()
  const isActive = checkIsActive(pathname, to)
  const iconPaths = icon ? SIDEBAR_ICONS[icon] : undefined

  return (
    <div className='menu-item'>
      <Link className={clsx('menu-link without-sub', {active: isActive})} to={to} onClick={onClick}>
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {iconPaths ? (
          <svg
            width='18px'
            height='18px'
            viewBox='0 0 24 24'
            fill='none'
            stroke={isActive ? '#fff' : '#99a1b7'}
            strokeWidth={1.75}
            strokeLinecap='round'
            strokeLinejoin='round'
            style={{opacity: isActive ? 1 : 0.8, marginRight: 14, marginLeft: 0, flexShrink: 0}}
            className='svg_custom_icon_sidebar'
            xmlns='http://www.w3.org/2000/svg'
          >
            {iconPaths}
          </svg>
        ) : (
          icon && (
            <span className='menu-icon'>
              <KTIcon iconName={icon} className='fs-2' />
            </span>
          )
        )}
        {fontIcon && <i className={clsx('bi fs-3', fontIcon)}></i>}
        <span className='menu-title'>{title}</span>
      </Link>
      {children}
    </div>
  )
}

export {AsideMenuItem}
